/*
 * VatomInc. Copyright (c) 2022, all rights reserved.
 *
 * This source code is licensed under the license found in the LICENSE file in the root directory of this source tree.
 */
import * as THREE from 'three'

import { ARViewerWeb } from '.'

/** Responsible for displaying debug info and buttons */
export default class DebugPlugin {
  // Vars
  ar: ARViewerWeb
  fps: number
  lastFPSCheck: number
  numFramesRendered: number
  stats: HTMLDivElement
  timer: NodeJS.Timeout | undefined

  /** Constructor */
  constructor(ar: ARViewerWeb) {
    // Store AR
    this.ar = ar

    // Frames per second count
    this.fps = 0
    this.lastFPSCheck = Date.now()
    this.numFramesRendered = 0

    this.stats = document.createElement('div')
    this.stats.style.cssText =
      'position: absolute; z-index: 10; top: 20px; right: 20px; max-width: 80%; background-color: rgba(0, 0, 0, 0.4); padding: 10px; color: white; font-size: 10px; line-height: 1.2; white-space: pre-wrap; '
  }

  /** Plugin name */
  get name() {
    return 'DebugPlugin'
  }

  /** Called on load */
  load(div: HTMLElement) {
    // Create stats panel
    div.appendChild(this.stats)

    // Start update timer
    this.timer = setInterval(this.update.bind(this), 100)

    // Create debug floor if necessary
    this.createDebugFloor()
  }

  /** Create debug floor */
  createDebugFloor() {
    // Stop if disabled
    if (!this.ar.getSetting('ar.floor-grid')) return

    // DEBUG: Test block one meter away
    // let geometry = new THREE.BoxGeometry(0.1, 2, 0.1)
    // let material = new THREE.MeshNormalMaterial()
    // let mesh = new THREE.Mesh(geometry, material)
    // mesh.position.y = 1
    // mesh.position.z = 1
    // this.scene.add(mesh)

    // Debug floor vertex shader
    const vertexShader = `
            varying vec3 vertex;
            varying vec3 camera;

            void main() {
                
                // Standard vertex shader, no manipulation of vertices
                gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                
                // Pass vertex position to fragment shader
                vertex = position;
                
                // Pass camera position to fragment shader
                camera = cameraPosition;
                
            }
        `

    // Debug floor fragment shader
    const fragmentShader = `
            varying vec3 vertex;
            varying vec3 camera;

            void main() {
                
                // Get fractional part of position
                float fx = fract(vertex.x);
                float fy = fract(vertex.y);
                
                // Calculate distance to camera
                float distance = length(camera.xzy - vertex);
                
                // Calculate fade based on camera distance (from 0.0 to 1.0)
                float fade = 1.0 - max(0.0, min(1.0, distance / 8.0));
                
                // Change color based on how close the position values are to an integer number
                if (fx < 0.01 || fy < 0.01)
                    gl_FragColor = vec4(0.2, 0.6, 0.2, fade);
                else
                    gl_FragColor = vec4(0.0, 0.0, 0.0, 0.0);
                
            }
        `

    // Debug floor plane
    const geometry = new THREE.PlaneGeometry(1000, 1000)
    const material = new THREE.ShaderMaterial({ vertexShader, fragmentShader })
    material.side = THREE.DoubleSide
    material.transparent = true
    const mesh = new THREE.Mesh(geometry, material)
    mesh.rotation.x = Math.PI / 2
    this.ar.scene.add(mesh)
  }

  unload() {
    // Stop update timer
    if (this.timer) clearInterval(this.timer)
  }

  /** Called on every frame rendered */
  onRender3D(delta: number) {
    // Count FPS
    this.numFramesRendered += 1
    if (this.lastFPSCheck + 1000 < Date.now()) {
      this.lastFPSCheck = Date.now()
      this.fps = this.numFramesRendered
      this.numFramesRendered = 0
    }
  }

  // Show debug info
  debugStats() {
    return `FPS: ${this.fps}`
  }

  /** Called to update the stats info */
  update() {
    // Get list of debug stats from each plugin
    const allInfo: any = []
    for (const plugin of this.ar.plugins) {
      if (plugin.debugStats) allInfo.push(plugin.debugStats())
    }

    // Show all stats
    this.stats.innerText = allInfo.join('\n')
  }
}
